import { type FC, useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  UiModal,
  UiModalOverlay,
  UiModalContent,
  UiModalHeader,
  UiModalCloseButton,
  UiModalBody,
  UiModalFooter,
  UiButton,
  UiStack,
  UiText,
  UiHStack,
  uiStyles,
  UiIconArrowsClockwise,
  UiIconEye,
  UiIconKey
} from '@/lib/ui';
import { 
  loadUserLastPasscode,
  requestNewPasscode,
  userLastPasscodeQueryKey,
  type UserLastPasscode 
} from '@/api/admin/user';
import { useTenantApi } from '@/account/hook/useTenantApi';
import BaseMessageBarError from '@/base/MessageBar/Error';
import BaseMessageBarInfo from '@/base/MessageBar/Info';
import dayjs from 'dayjs';

export interface PasscodeRevealModalProps {
  isOpen: boolean
  onClose: () => void
  userId: number
  userEmail: string
}

const PasscodeRevealModal: FC<PasscodeRevealModalProps> = ({
  isOpen,
  onClose,
  userId,
  userEmail
}) => {
  const { createTenantAdminApiRequest } = useTenantApi();
  const queryClient = useQueryClient();
  const [isRevealed, setIsRevealed] = useState(false);

  const { data, isFetching, error, refetch } = useQuery<UserLastPasscode, Error>({
    queryKey: [userLastPasscodeQueryKey, { id: userId }],
    queryFn: async (): Promise<UserLastPasscode> => {
      const response = await loadUserLastPasscode(createTenantAdminApiRequest)({ id: userId });
      return response.item;
    },
    enabled: isOpen && isRevealed,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: 'always'
  });

  const handleReveal = useCallback(() => {
    setIsRevealed(true);
  }, []);

  const handleRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  const [mutationError, setMutationError] = useState<string | null>(null);

  const requestNewMutation = useMutation({
    mutationFn: async () => {
      const response = await requestNewPasscode(createTenantAdminApiRequest)({ id: userId });
      return response.item;
    },
    onSuccess: (data) => {
      queryClient.setQueryData([userLastPasscodeQueryKey, { id: userId }], data);
      setMutationError(null);
    },
    onError: (error: Error) => {
      setMutationError(error.message || 'Failed to request new passcode');
    }
  });

  const handleRequestNew = useCallback(() => {
    requestNewMutation.mutate();
  }, [requestNewMutation]);

  const handleClose = useCallback(() => {
    setIsRevealed(false);
    setMutationError(null);
    void queryClient.invalidateQueries({ queryKey: [userLastPasscodeQueryKey, { id: userId }] });
    onClose();
  }, [onClose, queryClient, userId]);

  const formatPasscode = (passcode: string) => {
    return passcode;
  };

  const getTimeRemaining = (expiresAt?: string) => {
    if (!expiresAt) return null;
    const now = dayjs();
    const expiry = dayjs(expiresAt);
    const hoursRemaining = expiry.diff(now, 'hours');
    const minutesRemaining = expiry.diff(now, 'minutes') % 60;
    
    if (hoursRemaining > 0) {
      return `${hoursRemaining} hours ${minutesRemaining} minutes`;
    } else if (minutesRemaining > 0) {
      return `${minutesRemaining} minutes`;
    } else {
      return 'Expired';
    }
  };

  const getPasscodeDisplay = () => {
    if (!isRevealed) {
      return '••••••';
    }
    if (data?.valid && data?.passcode) {
      return formatPasscode(data.passcode);
    }
    return '------';
  };

  const isAnyLoading = isFetching || requestNewMutation.isLoading;

  const buttonConfig = useMemo(() => {
    if (!isRevealed) {
      return {
        onClick: handleReveal,
        label: 'Reveal Passcode',
        icon: <UiIconEye color="white" />
      };
    }
    if (data?.valid && data?.passcode) {
      return {
        onClick: handleRefresh,
        label: 'Refresh',
        icon: <UiIconArrowsClockwise color="white" />
      };
    }
    return {
      onClick: handleRequestNew,
      label: 'Request New Passcode',
      icon: <UiIconKey color="white" />
    };
  }, [isRevealed, data, handleReveal, handleRefresh, handleRequestNew]);

  return (
    <UiModal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      closeOnEsc={true}
      motionPreset="scale"
    >
      <UiModalOverlay />
      <UiModalContent borderRadius={uiStyles.borderRadius} p={2}>
        <UiModalHeader fontSize="title">Reveal Last Passcode</UiModalHeader>
        <UiModalCloseButton alignSelf="flex-end" p={4} size="lg" />
        
        <UiModalBody mt={{ base: 4, md: 0 }}>
          <UiStack spacing={4}>
            <BaseMessageBarInfo>
              Be careful when sharing this passcode in communications as they remain valid for 24 hours.
            </BaseMessageBarInfo>

            {(error || mutationError) && (
              <BaseMessageBarError>
                {mutationError || `Failed to fetch passcode: ${error?.message}`}
              </BaseMessageBarError>
            )}

            <UiStack spacing={4} alignItems="center" py={4}>
              <UiStack spacing={2} alignItems="center">
                <UiText color="text.secondary">Passcode</UiText>
                <UiText 
                  fontSize="3xl" 
                  fontWeight="bold" 
                  fontFamily="monospace"
                  textAlign="center"
                  py={2}
                  minHeight="60px"
                  display="flex"
                  alignItems="center"
                >
                  {getPasscodeDisplay()}
                </UiText>
              </UiStack>
              
              {isRevealed && data?.valid && data?.passcode && (
                <>
                  {data.createdAt && (
                    <UiStack spacing={1} alignItems="center">
                      <UiText variant="caption" color="text.secondary">Created</UiText>
                      <UiText variant="body2">{dayjs(data.createdAt).format('Do MMMM YYYY HH:mm')}</UiText>
                    </UiStack>
                  )}
                  
                  {data.expiresAt && (
                    <UiStack spacing={1} alignItems="center">
                      <UiText variant="caption" color="text.secondary">Expires</UiText>
                      <UiText variant="body2">
                        {dayjs(data.expiresAt).format('Do MMMM YYYY HH:mm')}
                        {' '}
                        ({getTimeRemaining(data.expiresAt)} remaining)
                      </UiText>
                    </UiStack>
                  )}
                </>
              )}

              {isRevealed && data && !data.valid && (
                <UiText variant="body2" color="text.secondary">
                  {data.message || 'No valid passcode found for this user.'}
                </UiText>
              )}
            </UiStack>
          </UiStack>
        </UiModalBody>
        
        <UiModalFooter>
          <UiButton
            onClick={buttonConfig.onClick}
            leftIcon={buttonConfig.icon}
            isLoading={isAnyLoading}
          >
            {buttonConfig.label}
          </UiButton>
        </UiModalFooter>
      </UiModalContent>
    </UiModal>
  );
};

export default PasscodeRevealModal;